import { Component, ElementRef, Inject, Input, OnChanges, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { UserService } from "@app2/account/user.service";
import { AlertTicketActionNotificationComponent } from "@app2/alert/alert-ticket-action-notification.component";
import { Domain } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { SearchClientService } from "@app2/clients/search-client.service";
import { TicketClientService } from "@app2/clients/ticket-client.service";
import { AlertStateChange, InboxAlert, MultipleAlertStateChangeRequest } from "@app2/clients/types/alert-typedefs";
import { AlertStoryAlertsTableComponent } from "@app2/alert/alert-inbox/alert-story-alerts-table.component";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import {
    CustomizeSearchColumnsModalComponent
} from "@app2/search/search-shared/customize-search-columns-modal.component";
import { localization } from "@app2/shared/localization/localization";
import { ModalService } from "@app2/shared/modal/modal.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { TimeService } from "@app2/shared/services/time.service";
import { AddEditTicketModalComponent } from "@app2/ticket/shared/add-edit-ticket-modal.component";
import { Ticket } from "@app2/type-defs/ticket/ticket-types";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import { saveAs } from "file-saver";
import * as _ from "underscore";

const CREATE_TICKET_MODAL_CANCELLED = "CREATE_TICKET_MODAL_CANCELLED";

@Component({
    selector: "alert-story-mode-viewer",
    template: `
        <div class="inbox-left-container">
            <header class="inbox-left-header">
                {{ "RECENT_AGGREGATE_VALUES" | localize | uppercase }}
            </header>
            <div *dsLoading="loading"
                 class="aggregate-selector-container"
                 test-id="aggregate-selector">
                <div *ngIf="allAggregations.length === 0"
                     class="vh-center-content"
                     test-class="no-more-aggregations">
                    {{ "NO_MORE_AGGREGATE_VALUES" | localize }}
                </div>
                <ul #aggregationList
                    *ngIf="allAggregations.length"
                    class="no-list-styles scrolling-panel"
                    test-id="aggregate-list">
                    <li class="aggregate-list-item click-target"
                        *ngFor="let aggregation of allAggregations"
                        (click)="updateAggregateValue(aggregation)"
                        [class.active]="aggregation === selectedAggregateValue"
                        test-class="aggregation"
                        [attr.test-id]="aggregation">
                        <div class="clearfix">
                            {{ aggregation }}
                        </div>
                    </li>
                </ul>
                <div class="flex-container flex-right return-to-top-container">
                    <ds-button type="blue-link"
                               [disabled]="!canScrollList()"
                               (click)="scrollToTopOfList()">
                        {{ "RETURN_TO_TOP" | localize }} <i class="far fa-arrow-up return-to-top-icon"></i>
                    </ds-button>
                </div>
            </div>
        </div>
        <header class="content-header">
            <search-box placeholderKey="ALERT_STORY_SEARCH"
                        [searchText]="queryText"
                        (searchTextChange)="changeQuery($event)">
            </search-box>
        </header>
        <div class="inbox-right-container position-relative">
            <section id="right-alert-list">
                <h2>
                    <div class="aggregate-header-container">
                        <span class="aggregate-title">{{ "AGGREGATE_VALUE_LABEL" | localize }}:</span>
                        <span test-id="aggregate-header">{{ aggregateValue }}</span>
                    </div>
                    <pr-dropdown align="right" class="export-dropdown" [matTooltip]="'GENERIC_DOWNLOAD_CSV' | localize">
                        <i *ngIf="downloadingEvents" class="fa fa-spinner fa-spin"></i>
                        <div pr-dropdown-trigger>
                            <ds-button type="blue-link"
                                       test-id="download-button">
                                <i *ngIf="!downloadingEvents" class="far fa-ellipsis-h"></i>
                            </ds-button>
                        </div>
                        <ul pr-dropdown-body>
                            <span>{{ "EXPORT" | localize | uppercase }}</span>
                            <li (click)="downloadCsv('Alerts')">
                                {{ "ALERTS_DOWNLOAD_CSV" | localize }}
                            </li>
                            <li (click)="downloadCsv('Events')">
                                {{ "EVENTS_DOWNLOAD_CSV" | localize }}
                            </li>
                        </ul>
                    </pr-dropdown>
                </h2>
                <div class="story-table-header">
                    <ul class="tab-group">
                        <li class="table-title"
                            (click)="isAlertsTab = true"
                            [class.active]="isAlertsTab">
                            <span class="title-text">{{ "ALERTS" | localize }}</span>
                            <span class="table-count">{{ ackedAndNewAlertIds.length }}</span>
                        </li>
                        <li class="table-title"
                            (click)="isAlertsTab = false"
                            [class.active]="!isAlertsTab">
                            <span class="title-text">{{ "EVENTS" | localize }}</span>
                            <span class="table-count">{{ eventNum }}</span>
                        </li>
                    </ul>
                    <ul class="button-group">
                        <li *ngIf="isAlertsTab">
                            <escalate-dropdown class="temporary-for-demo-hacky-get-rid-off-border"
                                               *dsPermissionsRequired="'ALERT_INBOX_EDIT'"
                                               [multipleAlerts]="true"
                                               buttonTooltip="ESCALATE_30D"
                                               [loading]="ackedAndNewAlertIds.length===0"
                                               (onEscalate)="escalateAlerts($event)">
                            </escalate-dropdown>
                        </li>
                        <li *ngIf="!isAlertsTab">
                            <a class="link-text"
                               (click)="openCustomizeSearchColumnsModal()"
                               test-id="custom-columns-selector">
                                <i class="far fa-columns"></i>
                                {{ "SEARCH_CUSTOMIZE_COLUMNS" | localize }}
                            </a>
                        </li>
                    </ul>
                </div>
                <alert-story-alerts-table class="full-width"
                                          [hidden]="!isAlertsTab"
                                          [aggregateValue]="aggregateValue"
                                          (onNewAndAckedAlertIdsChanged)="updateNewAndAckedAlertIds($event)">
                </alert-story-alerts-table>
                <events-table
                    [hidden]="isAlertsTab"
                    [queryString]="wrappedAggregateValue"
                    [begin]="eventBegin"
                    [end]="eventEnd"
                    [customColumns]="customColumns"
                    (onEventsFetched)="eventNum = $event">
                </events-table>
                <div *ngIf="!isAlertsTab" class="alert-table-footer">
                    <ds-button type="blue-link">
                        <a class="link-text"
                           target="_blank"
                           dsRouterLink="/search"
                           [queryParams]="{ query: wrappedAggregateValue, intervalType: 'LAST_24_HOURS' }">
                            {{ "SEARCH_AROUND_EVENT" | localize }}
                        </a>
                    </ds-button>
                </div>
            </section>
        </div>
    `,
    host: {
        style: "display: contents;",
    },
})
export class AlertStoryModeViewerComponent implements OnInit, OnChanges {
    @Input() selectedTriggerId: uuid;
    @Input() selectedAggregateValue: string;
    @ViewChild(AlertStoryAlertsTableComponent) alertsTableComponent: AlertStoryAlertsTableComponent;
    @ViewChild("aggregationList") aggregationList: ElementRef;

    queryText: string;
    aggregateValue: string;
    wrappedAggregateValue: string;
    ackedAndNewAlertIds: uuid[] = [];
    sampleTriggerId: uuid;
    downloadingEvents: boolean = false;
    alerts: InboxAlert[];
    allAggregations: string[] = [];
    loading: boolean;
    isAlertsTab: boolean = true;
    customColumns: string[] = [];
    eventBegin: Moment;
    eventEnd: Moment;
    eventNum: number = 0;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private timeService: TimeService,
                private alertClientService: AlertClientService,
                private ticketClientService: TicketClientService,
                private userService: UserService,
                private modalService: ModalService,
                private notificationsService: NotificationsService,
                private searchClientService: SearchClientService,
                private viewContainerRef: ViewContainerRef) {
    }

    ngOnInit() {
        this.refreshAggregations();
        this.eventEnd = this.timeService.now();
        this.eventBegin = this.eventEnd.clone().subtract(1, "days");
    }

    ngOnChanges() {
        if (this.selectedAggregateValue) {
            this.updateAggregateValue(this.selectedAggregateValue);
        }
    }

    updateAggregateValue(value: string) {
        this.aggregateValue = value;
        this.wrappedAggregateValue = "\"" + value + "\"";
    }

    updateNewAndAckedAlertIds({alerts, alertIds, sampleTriggerId}) {
        this.alerts = alerts;
        this.ackedAndNewAlertIds = alertIds;
        this.sampleTriggerId = sampleTriggerId;
    }

    changeQuery(queryText: string) {
        this.queryText = queryText;
        this.updateAggregateValue(this.queryText);
    }

    downloadCsv(csvType: string) {
        if (csvType === "Alerts") {
            const header = ["NAME", "CREATED", "COUNT", "STATE"].map(s => localization.getString(s)).join(",");
            const csv = header + "\n" + this.alerts.map((alert) => {
                return [alert.trigger !== undefined ? alert.trigger.name : "", alert.resultTimestamp, alert.resultCount, alert.currentState].join(",");
            }).join("\n");
            const blob = new Blob([csv], {type: "text/csv"});
            this.downloadingEvents = true;
            saveAs(blob, `DefenseStorm-AlertStory-Alerts-${this.aggregateValue}.csv`);
            this.downloadingEvents = false;
        } else {
            const request = {
                queryString: this.wrappedAggregateValue,
                begin: this.eventBegin,
                end: this.eventEnd,
                pageSize: 50_000,
            };
            this.downloadingEvents = true;
            this.searchClientService.downloadCsv(request)
                .then(blob => saveAs(blob, `DefenseStorm-AlertStory-Events-${ this.timeService.now().format(this.timeService.formats.timestampDate) }-${ this.aggregateValue }.csv`))
                .catch(error => {
                    const message = getMessageForError(error.error, "CSV_DOWNLOAD_ERROR");
                    this.notificationsService.showError(message, error);
                })
                .finally(() => this.downloadingEvents = false);
        }
    }

    private getMultipleAlertStateChangeRequest(slugName, slugId, alertIds): MultipleAlertStateChangeRequest {
        const alertStateChange: AlertStateChange = {
            newState: "ESCALATED",
            ticketSlugName: slugName,
            ticketSlugId: slugId,
        };
        return {
            alertStateChange,
            alertIds,
        };
    }

    public escalateAlerts({ slugName, slugId }: any): void {
        if (!this.sampleTriggerId || !this.ackedAndNewAlertIds) {
            //This condition shouldn't ever be hit in practice, but just in case, fail fast
            return;
        }
        const existingTicket: boolean = slugName && slugId;
        // Attach the alerts to a ticket, and then update the alerts
        (existingTicket
                ? this.escalateToExistingTicket(this.ackedAndNewAlertIds, slugName, slugId, this.sampleTriggerId)
                : this.escalateToNewTicket(this.ackedAndNewAlertIds, slugName, this.sampleTriggerId))
            .then(ticket => {
                    this.notificationsService.showComponent(AlertTicketActionNotificationComponent, {
                        key: existingTicket ? "TICKET_MODIFIED_SUCCESS_MULTIPLE" : "TICKET_CREATE_SUCCESS",
                        slugId: ticket.slugId,
                        slugName: ticket.slugName,
                        updatedAlerts: null,
                    });
                })
            .catch(err => {
                if (err === CREATE_TICKET_MODAL_CANCELLED) {
                    // Not an error, the operation was cancelled by the user
                    return;
                }
                const errorMessage = getMessageForError(err.error, "ALERT_STATE_CHANGE_ERROR");
                this.notificationsService.showError(errorMessage, err);
            })
            .finally(() => this.alertsTableComponent.refreshAlerts());
    }

    private escalateToExistingTicket(newAlertIds: uuid[], ticketSlugName, ticketSlugId, triggerId): Promise<Ticket> {
        return this.ticketClientService.getTicket(ticketSlugName, ticketSlugId)
            .then(ticket => {
                const currentAlertIds = ticket.alerts || [];
                const allAlerts = _.uniq([...currentAlertIds, ...newAlertIds]);
                return this.ticketClientService.updateTicket(ticket,
                    {
                        alertIds: allAlerts,
                        trigger: triggerId,
                    });
            })
            .then(ticket => {
                const newStateChange = this.getMultipleAlertStateChangeRequest(ticketSlugName, ticket.slugId,
                    newAlertIds);
                return this.alertClientService.escalateNewAndAckedAlerts(this.aggregateValue, newStateChange)
                    .then(() => ticket);
            });
    }

    private escalateToNewTicket(alertIds: uuid[], ticketSlugName, triggerId: uuid): Promise<Ticket> {
        const slugName = ticketSlugName;
        const currentUserId = this.userService.getCurrentUser().id;
        const blankTicket = TicketClientService.getBlankTicket(slugName, currentUserId);
        return this.modalService
            .open(AddEditTicketModalComponent, {
                    modalTitleKey: slugName === "task" ? "TASK_ADD_MODAL_TITLE" : "INCIDENT_ADD_MODAL_TITLE",
                    ticket: {
                        ...blankTicket,
                        summary: localization.getString("ALERT_STORY_TICKET_DESCRIPTION",
                            this.aggregateValue),
                        trigger: triggerId,
                        alertIds,
                    },
                }, { disableClose: true })
            .afterClosed()
            .toPromise()
            .then((createdTicket: Ticket) => {
                if (!createdTicket) {
                    // If the modal is cancelled, don't do anything.
                    // Can't use `afterClosedWithResult` because the `toPromise` will anyways return `undefined`.
                    return Promise.reject(CREATE_TICKET_MODAL_CANCELLED);
                }
                const newStateChange = this.getMultipleAlertStateChangeRequest(ticketSlugName, createdTicket.slugId,
                    alertIds);
                return this.alertClientService.escalateNewAndAckedAlerts(this.aggregateValue, newStateChange)
                    .then(() => createdTicket);
            });
    }

    private refreshAggregations(): void {
        this.loading = true;
        this.allAggregations = [];
        this.alertClientService.getTopAggregationsForOrg()
            .then((aggregations) => {
                this.allAggregations = aggregations;
                if (!this.selectedAggregateValue && this.allAggregations.length > 0) {
                    this.updateAggregateValue(this.allAggregations[0]);
                }
            })
            .catch(error => {
                const message = getMessageForError(error.error, "ERROR_LOADING_TOP_AGGREGATIONS");
                this.notificationsService.showError(message, error);
            })
            .finally(() => this.loading = false);
    }

    canScrollList(): boolean {
        return this.aggregationList?.nativeElement.scrollHeight > this.aggregationList?.nativeElement.clientHeight
            && this.aggregationList?.nativeElement.scrollTop > 0;
    }

    scrollToTopOfList(): void {
        this.aggregationList.nativeElement.scrollTop = 0;
    }

    openCustomizeSearchColumnsModal(): void {
        this.modalService
            .open(CustomizeSearchColumnsModalComponent, {
                customColumns: this.customColumns,
            }, { viewContainerRef: this.viewContainerRef })
            .afterClosedWithResult()
            .subscribe(customColumns => this.customColumns = customColumns);
    }
}
